/**
 * Денежные суммы: цифрами, прописью, с копейками и согласованием (ТЗ FR-38, правило R-04).
 *
 * Эталон из ТЗ:
 *   509 200 (Пятьсот девять тысяч двести) рублей 78 копеек
 *
 * Все суммы приходят в копейках целым числом, поэтому «потерянная копейка»
 * между текстом заявления и таблицей кредиторов невозможна по построению.
 */

type PluralForms = [one: string, few: string, many: string];

/** Выбор формы слова по числу: 1 рубль, 2 рубля, 5 рублей. */
export function plural(n: number, forms: PluralForms): string {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return forms[2];
  if (last > 1 && last < 5) return forms[1];
  if (last === 1) return forms[0];
  return forms[2];
}

const UNITS_MALE = ['', 'один', 'два', 'три', 'четыре', 'пять', 'шесть', 'семь', 'восемь', 'девять'];
const UNITS_FEMALE = ['', 'одна', 'две', 'три', 'четыре', 'пять', 'шесть', 'семь', 'восемь', 'девять'];
const TEENS = [
  'десять', 'одиннадцать', 'двенадцать', 'тринадцать', 'четырнадцать',
  'пятнадцать', 'шестнадцать', 'семнадцать', 'восемнадцать', 'девятнадцать',
];
const TENS = ['', '', 'двадцать', 'тридцать', 'сорок', 'пятьдесят', 'шестьдесят', 'семьдесят', 'восемьдесят', 'девяносто'];
const HUNDREDS = ['', 'сто', 'двести', 'триста', 'четыреста', 'пятьсот', 'шестьсот', 'семьсот', 'восемьсот', 'девятьсот'];

/** Разряды: форма слова и род числительного в этом разряде. */
const SCALES: Array<{ forms: PluralForms; female: boolean }> = [
  { forms: ['', '', ''], female: false }, // единицы — слово подставляется снаружи
  { forms: ['тысяча', 'тысячи', 'тысяч'], female: true },
  { forms: ['миллион', 'миллиона', 'миллионов'], female: false },
  { forms: ['миллиард', 'миллиарда', 'миллиардов'], female: false },
  { forms: ['триллион', 'триллиона', 'триллионов'], female: false },
];

/** Трёхзначная группа прописью. */
function tripletToWords(n: number, female: boolean): string[] {
  const words: string[] = [];
  const h = Math.floor(n / 100);
  const rest = n % 100;
  const t = Math.floor(rest / 10);
  const u = rest % 10;

  if (h > 0) words.push(HUNDREDS[h] as string);
  if (t === 1) {
    words.push(TEENS[u] as string);
  } else {
    if (t > 1) words.push(TENS[t] as string);
    if (u > 0) words.push(((female ? UNITS_FEMALE : UNITS_MALE)[u]) as string);
  }
  return words;
}

/**
 * Целое число прописью. `female` задаёт род последнего разряда единиц
 * (нужен для «одна копейка», «две копейки»).
 */
export function numberToWords(value: number, female = false): string {
  const n = Math.abs(Math.trunc(value));
  if (n === 0) return 'ноль';

  const triplets: number[] = [];
  let rest = n;
  while (rest > 0) {
    triplets.push(rest % 1000);
    rest = Math.floor(rest / 1000);
  }
  if (triplets.length > SCALES.length) throw new Error('Сумма слишком велика для вывода прописью');

  const parts: string[] = [];
  for (let scale = triplets.length - 1; scale >= 0; scale--) {
    const triplet = triplets[scale] as number;
    if (triplet === 0) continue;
    const info = SCALES[scale] as (typeof SCALES)[number];
    const isFemale = scale === 0 ? female : info.female;
    parts.push(...tripletToWords(triplet, isFemale));
    if (scale > 0) parts.push(plural(triplet, info.forms));
  }

  const result = parts.filter(Boolean).join(' ');
  return (value < 0 ? 'минус ' : '') + result;
}

export function capitalize(text: string): string {
  return text.length === 0 ? text : text[0]!.toUpperCase() + text.slice(1);
}

/** Разделение разрядов неразрывным пробелом: 509 200. */
export function formatDigits(value: number): string {
  return Math.abs(value)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

export interface MoneyParts {
  rubles: number;
  kopecks: number;
  /** «509 200» */
  digits: string;
  /** «509 200,78» */
  digitsWithKopecks: string;
  /** «Пятьсот девять тысяч двести» */
  words: string;
  /** «рублей» */
  rublesWord: string;
  /** «копеек» */
  kopecksWord: string;
  /** «509 200 (Пятьсот девять тысяч двести) рублей 78 копеек» */
  full: string;
}

/** Разбор суммы в копейках на все формы, используемые в шаблонах. */
export function money(kopecksTotal: number): MoneyParts {
  const total = Math.round(kopecksTotal);
  const rubles = Math.trunc(total / 100);
  const kopecks = Math.abs(total % 100);

  const words = capitalize(numberToWords(rubles));
  const rublesWord = plural(rubles, ['рубль', 'рубля', 'рублей']);
  const kopecksWord = plural(kopecks, ['копейка', 'копейки', 'копеек']);
  const digits = formatDigits(rubles);

  return {
    rubles,
    kopecks,
    digits,
    digitsWithKopecks: `${digits},${String(kopecks).padStart(2, '0')}`,
    words,
    rublesWord,
    kopecksWord,
    full: `${digits} (${words}) ${rublesWord} ${String(kopecks).padStart(2, '0')} ${kopecksWord}`,
  };
}

/** Короткая форма для интерфейса и таблиц: «509 200,78 руб.» */
export function moneyShort(kopecksTotal: number): string {
  return money(kopecksTotal).digitsWithKopecks + ' руб.';
}
